import { DAEMON, useBrowserOnlyInjectSaga } from '@dbh/redux-extra';
import { select, all, spawn, call, put, fork, takeLatest, takeEvery } from 'redux-saga/effects';
import { makeSelectIsBot } from '@dbh/ssr-data-redux';
import throwInServerSideRendering from '@dbh/throw-in-server-side-rendering';
import { makeSelectSrpCurrentPageHotelsList, HOTELS_LOADED, HOTEL_PACKAGES_LOADED } from '@dbh/hotels-redux';
import { makeSelectShoppingCartDataAndStatus, SHOPPING_CART_LOAD_SUCCESS, SHOPPING_CART_CREATE_SUCCESS, SHOPPING_CART_HAS_CHANGED_ITEM_QUANTITIES } from '@dbh/shopping-carts-redux';
import { GTAG_RELATED_PIXELS, trackingScriptsInjected, TRACKING_SCRIPTS_MANAGER_ACTIVE_FOR_BOTS, TRACKING_SCRIPTS_INJECTED, CAN_FIRE_PAGE_VIEW_TRACKING_EVENT, TRACKING_SCRIPTS_MANAGER_DISABLED, handleTrackingScriptsManagerDisabled, makeSelectIsTrackingScriptsManagerEnabled, REDUX_AND_SAGA_KEY } from '@dbh/tracking-scripts-redux';
import { microsoftClarityScriptStatusChanged, makeSelectMicrosoftClarityProjectId, injectMicrosoftClarityScript, MICROSOFT_CLARITY_SCRIPT_CAN_BE_INJECTED } from '@dbh/tracking-microsoft-clarity';
import invariant from 'invariant';
import { PAGE_TYPES, PAGE_TYPES_THAT_USE_SEARCH_RESULTS_PAGE_TEMPLATE } from '@dbh/admindev-constants';
import { makeSelectTemplateCode } from '@dbh/webpages-redux';
import { fireCriteoPixelShoppingCartLoaded, fireCriteoPixelViewHomePage, fireCriteoPixelSearchPages, fireCriteoPixelPackagesLoaded, injectCriteoScript } from '@dbh/tracking-criteo';
import { fireFacebookPixelShoppingCartLoaded, fireFacebookPixelPageView, fireFacebookPixelSearchPages, fireFacebookPixelPackagesLoaded, fireFacebookPixelShoppingCartCreated } from '@dbh/tracking-facebook';
import { fireAdwordsPixelPageView } from '@dbh/tracking-adwords';
import { fireAwinJourney, injectAwinScript } from '@dbh/tracking-awin';
import { fireBingPixelPageView, injectBingScript } from '@dbh/tracking-bing';
import { fireGtagPageView, injectGtagScript } from '@dbh/tracking-gtag';
import { fireShoppingCartCreated, fireAddOrRemoveFromCart } from '@dbh/tracking-enhanced-commerce';
import 'prop-types';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import { makeSelectIsFirstPageAfterSsr } from '@dbh/routing-redux';
import Cookie from 'universal-cookie';
import isGDPRComplianceModeDisabled from '@dbh/is-gdpr-compliance-mode-disabled';
import { makeSelectPixelCustomizations } from '@dbh/site-customizations-redux';
import { injectHotjarScript } from '@dbh/tracking-hotjar';
import { makeSelectGoogleAnalyticsId, getActiveGoogleAnalyticsVersionsFromDomOnce } from '@dbh/analytics';
import { PIXELS } from '@dbh/daybreak-hero-redux';
import { getActiveConsentGroups } from '@dbh/cookie-consent';
import { CONSENT_GROUPS } from '@dbh/cookie-consent-utils';

/**
 * Fires all "shopping cart loaded pixels" (`BookingFormPage`).
 * @yields {Object[]} .
 */function*fireShoppingCartLoadedPixels(){yield all([spawn(fireFacebookPixelShoppingCartLoaded),spawn(fireCriteoPixelShoppingCartLoaded)]);}/**
 * Checks whether `fireShoppingCartLoadedPixels` should be called or not. We
 * don't call them if this is the "landing page" or the shopping cart is not
 * loaded.
 * @param {boolean} isFirstPageAfterSsr Is this a "landing page"? Passed by us.
 * @yields {Object[]?} .
 */function*handleShoppingCartLoaded(a){throwInServerSideRendering("handleHotelPackagesLoaded");const b=yield select(makeSelectTemplateCode());if(b!==PAGE_TYPES.BookingFormPage)return;const c=yield select(makeSelectShoppingCartDataAndStatus()),d=c.get("loadedData");a&&!d||(// Note that we accept `isShoppingCartLoaded` to be `false` only when it is
// the "first page after SSR" (landing page), as this saga is called when "pixels
// manager" is enabled in such page. Otherwise, this saga is called on
// `SHOPPING_CART_LOAD_SUCCESS` and the shopping cart must be loaded, otherwise
// there is a programming error if we dispatched `SHOPPING_CART_LOAD_SUCCESS`
// but `makeSelectShoppingCartDataAndStatus` returns `false`.
invariant(!!d,"(`handleDataLayerPushForShoppingCartLoaded`) Expected the shopping cart to be loaded."),yield fireShoppingCartLoadedPixels());}

/**
 * Fires page view tracking scripts (we also call them pixels).
 * @param {Object} event .
 * @param {Object} event.payload .
 * @param {string} event.payload.templateCode .
 * @yields {Object[]?} .
 */function*firePageViewPixels(a){const{payload:{templateCode:b}}=a;yield spawn(fireGtagPageView),[PAGE_TYPES.TopMenuHashPage,PAGE_TYPES.HashPage].includes(b)||(yield all([spawn(fireAwinJourney),spawn(fireFacebookPixelPageView),spawn(fireCriteoPixelViewHomePage),spawn(fireAdwordsPixelPageView),spawn(fireBingPixelPageView)]));}

/**
 * Calls all "hotels loaded pixels".
 * Fires in page types: `SearchResultsPage`, `CategoryCityPage`, `PoiPage`.
 * @yields {Object[]?} .
 */function*fireHotelsLoadedPixels(){yield all([spawn(fireFacebookPixelSearchPages),spawn(fireCriteoPixelSearchPages)]);}/**
 * Calls the "hotels loaded" pixels, if the current page has the
 * `SearchResultsPage` template and has at least one hotel (the "no search
 * results page" is ignored).
 * @yields {Object[]?} .
 */function*handleHotelsLoaded(){throwInServerSideRendering("handleHotelsLoaded");const a=yield select(makeSelectTemplateCode());if(PAGE_TYPES_THAT_USE_SEARCH_RESULTS_PAGE_TEMPLATE[a]){const a=yield select(makeSelectSrpCurrentPageHotelsList());!a||a.isEmpty()||(yield fireHotelsLoadedPixels());}}

/**
 * Fires the "hotel packages loaded" pixels.
 * @yields {Object[]} .
 */function*fireHotelPackagesLoadedPixels(){yield all([spawn(fireFacebookPixelPackagesLoaded),spawn(fireCriteoPixelPackagesLoaded)]);}/**
 * Calls the "hotel packages loaded" pixels, if the current page is an `HdpPage`.
 * @yields {Object[]?} .
 */function*handleHotelPackagesLoaded(){const a=yield select(makeSelectTemplateCode());a!==PAGE_TYPES.HdpPage||(yield call(fireHotelPackagesLoadedPixels));}

/**
 * Fires the "add to cart" pixels when the shopping cart is created in the
 * browser.
 * @yields {Object[]} .
 */function*handleShoppingCartCreateSuccess(){throwInServerSideRendering("handleShoppingCartCreateSuccess"),yield all([spawn(fireShoppingCartCreated),spawn(fireFacebookPixelShoppingCartCreated)]);}

/**
 * Fires the events to track quantity changes in the shopping cart.
 * @param {Object} shoppingCartHasChangedQuantityEvent .
 * @throws {Error} If invoked in `SSR`.
 * @throws {Error} If the argument is not valid.
 */const handleShoppingCartHasChangedQuantity=withConformsTo("handleShoppingCartHasChangedQuantity",[],function*(a){yield spawn(fireAddOrRemoveFromCart,a);});var handleShoppingCartHasChangedQuantity$1 = handleShoppingCartHasChangedQuantity;

/**
 * Checks if this is the "first page after SSR" and only in that case it
 * calls the generators that fill the data layer and fire the CustomEvents.
 * We need to do this because, in the "first page after SSR", those generators
 * wouldn't be called otherwise, since the events we listen to with `takeLatest`
 * won't fire again in the browser, since they already fired in SSR.
 * @yields {Object[][]?} .
 */function*handleTrackingScriptsManagerInjected(){const a=yield select(makeSelectIsFirstPageAfterSsr());a&&(yield all([handleHotelsLoaded(),handleHotelPackagesLoaded(),handleShoppingCartLoaded(!0)]));}

function*injectTrackingScripts(){throwInServerSideRendering("injectTrackingScripts");const a=yield select(makeSelectPixelCustomizations()),b=a.get(PIXELS.BING);b&&injectBingScript();const c=a.get(PIXELS.HOTJAR);c&&injectHotjarScript();const d=a.get(PIXELS.MICROSOFT_CLARITY);if(d){const a=new Cookie,b=getActiveConsentGroups(a),c=b.includes(CONSENT_GROUPS.PERFORMANCE),d=c||isGDPRComplianceModeDisabled();yield put(microsoftClarityScriptStatusChanged(d));}// If any pixel that is depending on `gtag` script is enabled we have
// to inject the `gtag` script, then for the disabled pixels we conditionally
// fire their events in the sagas.
const e=GTAG_RELATED_PIXELS.some(b=>a.get(b));if(e){const a=yield select(makeSelectGoogleAnalyticsId()),{sendToGA4:b}=getActiveGoogleAnalyticsVersionsFromDomOnce();if(b){// The default ID is the `GA4` ID.
const[b]=a;yield injectGtagScript(b);}}const f=a.get(PIXELS.CRITEO);f&&(yield injectCriteoScript());const g=a.get(PIXELS.AWIN);g&&(yield injectAwinScript()),yield put(trackingScriptsInjected());}

/**
 * Injects `Microsoft Clarity` tracking script.
 *
 * Live test procedures:
 * A) **Without Cookies:**
 * A.1) Visit the website using incognito (with `Clarity` enabled, meaning
 * `DBH_SITE_TRACKING_FORCE_MICROSOFT_CLARITY_ENABLED=true` in the environment
 * variables of the `Docker` service of the site, on the `Linux` server).
 * A.2) Decline the performance Cookies.
 * A.3) Give it some time then use `Microsoft Clarity` platform.
 * @see {@link https://clarity.microsoft.com/projects}
 * A.4) Select the correct project.
 * A.5) You should see the current actions on the website in the live sessions.
 * A.6) Each page you visit or refresh will be recorded as a new session.
 * A.7) You should not see `_clsk` or `_clck` in the cookies inside "Application"
 * in `Chrome` `devtools`. @see {@link https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-list}
 *
 * B) **With Cookies:** (Consent was provided by the user to use the performance
 * cookies).
 * B.1) Visit the website using incognito (with `Clarity` enabled, meaning
 * `DBH_SITE_TRACKING_FORCE_MICROSOFT_CLARITY_ENABLED=true` in the environment
 * variables of the `Docker` service of the site, on the `Linux` server).
 * B.2) Accept the performance Cookies.
 * B.3) Give it some time then use `Microsoft Clarity` platform.
 * @see {@link https://clarity.microsoft.com/projects}
 * B.4) Select the correct project.
 * B.5) You should see the current actions on the website in the live sessions.
 * B.6) All the navigations or refreshes will be recorded in one session.
 * B.7) You should see `_clsk` or `_clck` in the cookies inside "Application"
 * in `Chrome` `devtools`. @see {@link https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-list}
 *
 * C) **With Cookies:** (Consent was provided by the user to use the performance
 * cookies, the `Microsoft Clarity`. Cookies should be generated if removed for
 * some reason).
 * C.1) Visit the website using incognito (with `Clarity` enabled, meaning
 * `DBH_SITE_TRACKING_FORCE_MICROSOFT_CLARITY_ENABLED=true` in the environment
 * variables of the `Docker` service of the site, on the `Linux` server).
 * C.2) Accept the performance Cookies.
 * C.3) Give it some time then use `Microsoft Clarity` platform.
 * @see {@link https://clarity.microsoft.com/projects}
 * C.4) Select the correct project.
 * C.5) You should see the current actions on the website in the live sessions.
 * C.6) All the navigations or refreshes will be recorded in one session.
 * C.7) You should see `_clsk` or `_clck` in the cookies inside "Application"
 * in `Chrome` `devtools`. @see {@link https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-list}
 * C.8) Now delete `_clsk` and `_clck` cookies and keep the cookies tab open.
 * c.9) Observe that the cookies are generated again by refreshing the page.
 *
 * D) **With Cookies:** (Consent was provided by the user to use the performance
 * cookies then declined it later).
 * D.1) Visit the website using incognito (with `Clarity` enabled, meaning
 * `DBH_SITE_TRACKING_FORCE_MICROSOFT_CLARITY_ENABLED=true` in the environment
 * variables of the `Docker` service of the site, on the `Linux` server).
 * D.2) Accept the performance Cookies.
 * D.3) Give it some time then use `Microsoft Clarity` platform.
 * @see {@link https://clarity.microsoft.com/projects}
 * D.4) Select the correct project.
 * D.5) You should see the current actions on the website in the live sessions.
 * D.6) All the navigations or refreshes will be recorded in one session.
 * D.7) You should see `_clsk` or `_clck` in the cookies inside "Application"
 * in `Chrome` `devtools`. @see {@link https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-list}
 * D.8) From the `DBH` development bar find `CONSENT ➞ | P | T | P + T | RESET`.
 * D.9) Click reset and wait for the page to refresh automatically.
 * D.10) Decline or don't accept the performance.
 * D.11) Observe the `_clsk` and `_clck` cookies are removed.
 * D.12) A new session will be recorded for each page now.
 * @param {Object} event .
 * @param {string} event.type .
 * @param {Object} event.payload .
 * @param {boolean} event.payload.consentAccepted .
 * @yields {void} .
 */function*handleMicrosoftClarityStatusChanged(a){const b=yield select(makeSelectPixelCustomizations()),c=b.get(PIXELS.MICROSOFT_CLARITY);if(!c)return;const{payload:{consentAccepted:d}}=a,e=new Cookie;if(!window.clarity){// The key is hardcoded only to discourage manipulating this cookie manually
// elsewhere: it should be managed by the `One Trust` script.
const a=e.get("OptanonAlertBoxClosed");if(a){const a=yield select(makeSelectMicrosoftClarityProjectId());// If the `OneTrust` widget is already closed, the script should be injected.
// This allows the recording of sessions after rendering the landing page
// and accepting the cookie consent inside it, with the `One Trust` popup.
// If the user has given consent, the function `window.clarity('consent')`
// will use cookies to track all navigations as a single session. Without
// consent, each page navigation will be recorded as a separate session.
yield injectMicrosoftClarityScript(a);}}window.clarity&&d&&window.clarity("consent");}

const selectIsTrackingScriptsManagerEnabled=makeSelectIsTrackingScriptsManagerEnabled();function*trackingScriptsManagerRootSaga(){if(throwInServerSideRendering("trackingScriptsManagerRootSaga"),!TRACKING_SCRIPTS_MANAGER_ACTIVE_FOR_BOTS){const a=yield select(makeSelectIsBot());if(a)return}const a=yield select(selectIsTrackingScriptsManagerEnabled);a&&(// This must be called only once, we don't want to inject the scripts multiple times.
yield fork(injectTrackingScripts),yield all([takeLatest(TRACKING_SCRIPTS_INJECTED,handleTrackingScriptsManagerInjected),takeLatest(CAN_FIRE_PAGE_VIEW_TRACKING_EVENT,firePageViewPixels),// `HdpPage`, pages using the `SearchResultsPage` template.
takeLatest(HOTELS_LOADED,handleHotelsLoaded),takeLatest(HOTEL_PACKAGES_LOADED,handleHotelPackagesLoaded),// `BookingFormPage`.
takeLatest(SHOPPING_CART_LOAD_SUCCESS,handleShoppingCartLoaded),takeLatest(SHOPPING_CART_CREATE_SUCCESS,handleShoppingCartCreateSuccess),takeLatest(TRACKING_SCRIPTS_MANAGER_DISABLED,handleTrackingScriptsManagerDisabled),takeLatest(MICROSOFT_CLARITY_SCRIPT_CAN_BE_INJECTED,handleMicrosoftClarityStatusChanged),takeEvery(SHOPPING_CART_HAS_CHANGED_ITEM_QUANTITIES,handleShoppingCartHasChangedQuantity$1)]));}const injectedSagaConfiguration={key:REDUX_AND_SAGA_KEY,mode:DAEMON};

const injectedSagaConfig={...injectedSagaConfiguration,saga:trackingScriptsManagerRootSaga},useBrowserOnlyInjectTrackingScriptsInvokerSaga=()=>useBrowserOnlyInjectSaga(injectedSagaConfig);var useBrowserOnlyInjectTrackingScriptsInvokerSaga$1 = useBrowserOnlyInjectTrackingScriptsInvokerSaga;

export { useBrowserOnlyInjectTrackingScriptsInvokerSaga$1 as useBrowserOnlyInjectTrackingScriptsInvokerSaga };
